import { Box, Text, Flex, useColorModeValue, useToast } from "@chakra-ui/react";
import { IoHeart } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import { AddLike } from "../services/backend";
import { GlobalContext } from "../store/global_context";
import { useMatchCelebration } from "../context/MatchCelebrationContext";
import * as motion from "motion/react-client";
import { AnimatePresence } from "motion/react";
import { resolveProfileImageUrl } from "../util/profileImage";
import { displayFirstName } from "../util/displayName";
import PremiumVerifiedBadge from "./ui/PremiumVerifiedBadge";
import { isUserPremium } from "../util/premiumAccess";
import { FREE_DAILY_LIKE_LIMIT, isLikeLimitError } from "../util/likeLimit";

const LikeCard = (props) => {
  const [isVisible, setIsVisible] = useState(true);
  const { celebrateFromLikeResponse } = useMatchCelebration();
  const globalCtx = useContext(GlobalContext);
  const navigate = useNavigate();
  const toast = useToast();
  const user = JSON.parse(localStorage.getItem("user"));

  const placeholderBg = useColorModeValue(
    "linear-gradient(160deg, #e2e8f0 0%, #cbd5e1 100%)",
    "linear-gradient(160deg, #27272a 0%, #18181b 100%)"
  );
  const placeholderLetter = useColorModeValue("blackAlpha.300", "whiteAlpha.350");
  const ringColor = useColorModeValue("rgba(52,211,153,0.35)", "rgba(74,222,128,0.42)");
  const cardShadow = useColorModeValue(
    "0 8px 24px rgba(15,23,42,0.1), 0 0 0 1px rgba(15,23,42,0.06)",
    "0 12px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08)"
  );
  const heartBtnBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(12,12,14,0.72)");
  const heartBtnColor = useColorModeValue("gray.800", "white");
  const heartBtnBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.250");

  function handleProfileClick() {
    navigate(`/like/${props.id}`);
  }

  async function handleOnLike(profile) {
    try {
      const likeResponse = await AddLike({
        LikerProfileId: user.id,
        LikedProfileId: profile.id,
      });
      setIsVisible(false);
      if (await celebrateFromLikeResponse(likeResponse, profile, { assumeMutualMatch: true })) {
        if (
          props.setLikesNotifications &&
          props.setMatchesNotifications &&
          props.likesNotifications &&
          props.matchesNotifications
        ) {
          const updatedLikes = props.likesNotifications.filter(
            (likeNotif) => likeNotif.user.id !== profile.id
          );
          props.setLikesNotifications(updatedLikes);
          const newMatchNotif = { user: profile };
          props.setMatchesNotifications([newMatchNotif, ...props.matchesNotifications]);
        }
        if (props.onSuccessfulMatch) {
          props.onSuccessfulMatch(profile.id);
        }
      }
      if (!isUserPremium(user)) {
        globalCtx.incrementLikeCount();
      }
    } catch (error) {
      console.error("Error in handleOnLike:", error);
      if (isLikeLimitError(error)) {
        globalCtx.updateLikeCount(FREE_DAILY_LIKE_LIMIT);
        toast({
          title: "Daily like limit reached",
          description: "Upgrade to Premium for unlimited likes.",
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "bottom-left",
        });
      }
    }
  }

  const imageUrl = resolveProfileImageUrl({
    profileImageUri: props.profileImage,
    images: props.images,
    firstName: props.firstName,
    lastName: props.lastName,
  });
  const showPhoto = Boolean(imageUrl) && !String(imageUrl).includes("ui-avatars.com");
  const initial = (props.firstName || "?").trim().charAt(0).toUpperCase() || "?";
  const ageLabel = props.age != null && props.age !== "" ? String(props.age) : null;
  const countryLabel = props.country ? String(props.country).trim() : "";

  return (
    <AnimatePresence initial={false}>
      {isVisible ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          key="like-card"
          style={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
        >
          <Box
            position="relative"
            borderRadius={{ base: "xl", lg: "2xl" }}
            overflow="hidden"
            aspectRatio={{ base: "3 / 4", lg: "auto" }}
            height={{ base: "auto", lg: "135px" }}
            width="100%"
            cursor="pointer"
            backgroundImage={showPhoto ? `url(${imageUrl})` : undefined}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            bg={showPhoto ? undefined : placeholderBg}
            boxShadow={cardShadow}
            onClick={handleProfileClick}
            _hover={{
              boxShadow: {
                base: cardShadow,
                lg: `0 12px 36px rgba(52,211,153,0.18), 0 0 0 2px ${ringColor}`,
              },
            }}
            transition="box-shadow 0.2s ease"
          >
            {!showPhoto && (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                pointerEvents="none"
              >
                <Text
                  fontSize={{ base: "4xl", lg: "3xl" }}
                  fontWeight="700"
                  color={placeholderLetter}
                  lineHeight={1}
                  userSelect="none"
                >
                  {initial}
                </Text>
              </Flex>
            )}

            <Box
              position="absolute"
              bottom={0}
              left={0}
              width="100%"
              height={{ base: "55%", lg: "72%" }}
              bgGradient="linear(to-t, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)"
              borderBottomRadius={{ base: "xl", lg: "2xl" }}
            />

            <Box
              as="button"
              type="button"
              position="absolute"
              top={2}
              right={2}
              zIndex={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxSize={{ base: "34px", lg: "28px" }}
              borderRadius="full"
              bg={heartBtnBg}
              color={heartBtnColor}
              borderWidth="1px"
              borderColor={heartBtnBorder}
              backdropFilter="blur(10px)"
              boxShadow="0 4px 14px rgba(0,0,0,0.2)"
              onClick={(e) => {
                e.stopPropagation();
                handleOnLike(props.user);
              }}
              _hover={{
                transform: "scale(1.05)",
              }}
              transition="transform 0.15s ease"
              aria-label="Like back"
            >
              <IoHeart size={15} />
            </Box>

            <Box
              position="absolute"
              zIndex={1}
              bottom={{ base: 2.5, lg: "10px" }}
              left={0}
              width="100%"
              pointerEvents="none"
              px={{ base: 2.5, lg: 3 }}
            >
              <Flex align="center" gap={1} minW={0}>
                <Text
                  m={0}
                  fontSize={{ base: "sm", lg: "sm" }}
                  fontWeight="700"
                  color="white"
                  letterSpacing="-0.01em"
                  textShadow="0 1px 10px rgba(0,0,0,0.55)"
                  noOfLines={1}
                  title={props.firstName || undefined}
                >
                  {displayFirstName(props.firstName, 14)}
                  {ageLabel ? (
                    <Text as="span" fontWeight="600" opacity={0.92}>
                      {`, ${ageLabel}`}
                    </Text>
                  ) : null}
                </Text>
                <PremiumVerifiedBadge user={props.user} show={isUserPremium(props.user) || props.isVerified} size={14} />
              </Flex>
              {countryLabel ? (
                <Text
                  m={0}
                  mt={0.5}
                  fontSize={{ base: "11px", lg: "10px" }}
                  fontWeight="500"
                  color="whiteAlpha.850"
                  textShadow="0 1px 8px rgba(0,0,0,0.5)"
                  noOfLines={1}
                >
                  {countryLabel}
                </Text>
              ) : null}
            </Box>
          </Box>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default LikeCard;
